import z from 'zod';
import * as _ from 'lodash-es';

import { translate } from '../utils/translations';

/**
 * 必須バリデーション
 * 空文字、半角全角スペースのみは不可
 * @type {ZodString}
 */
export const vRequired = z
  .string({
    required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
  })
  .trim()
  .refine((value) => value.trim() !== '', {
    message: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
  });

/**
 * 半角数字バリデーション
 * @type {ZodNumber}
 */
export const vNum = z.preprocess(
  (v) => (v ? String(v) : undefined),
  z
    .string({
      required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
    })
    .trim()
    .regex(/^\d+$/, {
      message: translate('validate:VALIDATE.MSG_ERROR_COMMON_006'),
    })
);

/**
 * 半角英字バリデーション
 * @type {ZodString}
 */
export const vAlphabet = z
  .string({
    required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
  })
  .trim()
  .regex(/^[a-zA-Z]+$/, {
    message: translate('validate:VALIDATE.MSG_ERROR_COMMON_013'),
  });

/**
 * 半角英数字バリデーション
 * @type {ZodString}
 */
export const vAlphabetNum = z
  .string({
    required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
  })
  .trim()
  .regex(/^[a-zA-Z0-9]+$/, {
    message: translate('validate:VALIDATE.MSG_ERROR_COMMON_007'),
  });

/**
 * ひらがなバリデーション
 * @type {ZodString}
 */
export const vHiragana = z
  .string({
    required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
  })
  .trim()
  .regex(/^[ぁ-んゔー]+$/, {
    message: translate('validate:VALIDATE.MSG_ERROR_COMMON_008'),
  });

/**
 * カタカナバリデーション
 * @type {ZodString}
 */
export const vKatakana = z
  .string({
    required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
  })
  .trim()
  .regex(/^[ァ-ヶー]+$/, {
    message: translate('validate:VALIDATE.MSG_ERROR_COMMON_009'),
  });

/**
 * 最小文字数バリデーション
 * 最小桁数以上かチェックする（ex: 10桁以上か？）
 * @param {number} min - 最小文字数
 * @returns {ZodString}
 */
export const vMin = (min: number) =>
  z
    .string({
      required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
    })
    .trim()
    .min(min, {
      message: translate('validate:VALIDATE.MSG_ERROR_COMMON_010', { min }),
    });

/**
 * 最大文字数バリデーション
 * 最大桁数以下かチェックする（ex: 10桁以下か？）
 * @param {number} max - 最大文字数
 * @returns {ZodString}
 */
export const vMax = (max: number) =>
  z
    .string({
      required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
    })
    .trim()
    .max(max, {
      message: translate('validate:VALIDATE.MSG_ERROR_COMMON_011', { max }),
    });

/**
 * 最小文字数と最大文字数の範囲バリデーション
 * @param {number} min - 最小文字数
 * @param {number} max - 最大文字数
 * @returns {ZodString}
 */
export const vMinMax = (min: number, max: number) =>
  z
    .string({
      required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
    })
    .trim()
    .min(min, {
      message: translate('validate:VALIDATE.MSG_ERROR_COMMON_012', {
        min,
        max,
      }),
    })
    .max(max, {
      message: translate('validate:VALIDATE.MSG_ERROR_COMMON_012', {
        min,
        max,
      }),
    });

/**
 * 日付バリデーション
 * @type {ZodString}
 */
export const vDate = z
  .string({
    required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
  })
  .trim()
  .regex(/^[0-9]{4}\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/, {
    message: translate('validate:VALIDATE.MSG_ERROR_COMMON_015'),
  });

/**
 * 時刻バリデーション
 * @type {ZodString}
 */
export const vTime = z
  .string({
    required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
  })
  .trim()
  .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/, {
    message: translate('validate:VALIDATE.MSG_ERROR_COMMON_016'),
  });

/**
 * 日時バリデーション
 * @type {ZodString}
 */
export const vDateTime = z
  .string({
    required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
  })
  .trim()
  .regex(
    /^[0-9]{4}\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\s([01][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/,
    {
      message: translate('validate:VALIDATE.MSG_ERROR_COMMON_017'),
    }
  );

/**
 * 最小数値バリデーション
 * @param {number} min - 最小範囲値
 * @returns {ZodNumber}
 */
export const vMinRange = (min: number) =>
  z
    .number({
      required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
      invalid_type_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_018', {
        min,
      }),
    })
    .gte(min, {
      message: translate('validate:VALIDATE.MSG_ERROR_COMMON_018', { min }),
    });

/**
 * 最大数値バリデーション
 * @param {number} max - 最大範囲値
 * @returns {ZodNumber}
 */
export const vMaxRange = (max: number) =>
  z
    .number({
      required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
      invalid_type_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_019', {
        max,
      }),
    })
    .lte(max, {
      message: translate('validate:VALIDATE.MSG_ERROR_COMMON_019', { max }),
    });

/**
 * Emailバリデーション
 * @type {ZodString}
 */
export const vEmail = z
  .string({
    required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
  })
  .trim()
  .regex(
    /^([a-zA-Z0-9])+([.+a-zA-Z0-9_-])*@([a-zA-Z0-9_-])+(\.[a-zA-Z0-9_-]+)+$/,
    {
      message: translate('validate:VALIDATE.MSG_ERROR_COMMON_021'),
    }
  );

/**
 * 特定の文字数のみ許可するバリデーション
 * @param {number} len - 許可する文字数
 * @returns {ZodString}
 */
export const vXCharOnly = (len: number) =>
  z
    .string({
      required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
    })
    .trim()
    .refine(
      (arg) => {
        const argNoSpace = arg.replace(/\s/g, '');
        if (argNoSpace.length !== arg.length) {
          return false;
        }

        return argNoSpace.length === len;
      },
      {
        message: translate('validate:VALIDATE.MSG_ERROR_COMMON_022', { len }),
      }
    );

/**
 * 全角カナ、英数字バリデーション
 * @type {ZodString}
 */
export const vKanaAlphabetNum = z
  .string({
    required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
  })
  .trim()
  .regex(/[ァ-ヶーa-zA-Z0-9]+/g, {
    message: translate('validate:VALIDATE.MSG_ERROR_COMMON_023'),
  });

/**
 * 必須バリデーション
 * 空文字、半角全角スペースのみは不可
 * @type {ZodString}
 */
export const vRequiredMax = (max: number) =>
  z
    .string({
      required_error: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
    })
    .trim()
    .max(max, {
      message: translate('validate:FORM_VALIDATE.TOO_BIG', { max }),
    })
    .refine((value) => value.trim() !== '', {
      message: translate('validate:VALIDATE.MSG_ERROR_COMMON_005'),
    });

/**
 * 複数のバリデーション関数でエラーメッセージをまとめて取得する関数
 * @param validators validateUtils.tsで定義しているバリデーション関数のリスト
 * @param value 検証したい値
 * @returns string化されたエラーメッセージ 複数の場合は改行(\n)で区切られる
 */
export const getValidationErrors = (
  validators: Array<
    | z.ZodString
    | z.ZodNumber
    | z.ZodDate
    | z.ZodEffects<z.ZodString, string, unknown>
  >,
  value: string | number | Date
): string => {
  const errorMessages: string[] = [];

  validators.forEach((validator) => {
    const result = validator.safeParse(value);
    if (!result.success) {
      result.error.issues.forEach((issue) => {
        errorMessages.push(issue.message);
      });
    }
  });

  // "Expected xxxx, received null"を「必須です。」に置換する
  const outputMessages = _.map(_.uniq(errorMessages), (message) =>
    _.replace(
      message,
      /Expected \S+, received null/,
      translate('validate:VALIDATE.MSG_ERROR_COMMON_005')
    )
  );

  return _.uniq(outputMessages).join('\n');
};

export const vFileFolderName = z
  .string()
  .trim()
  .min(1, { message: translate('validate:VALIDATE.MSG_ERROR_COMMON_005') })
  .max(255, {
    message: translate('validate:VALIDATE.MSG_ERROR_COMMON_011', {
      max: 255,
    }),
  })
  .regex(/^[^<>:"/\\|?*]+$/, { message: '禁止文字が含まれています' }); // 禁止文字(<, >, :, ", /, \, |, ?, *)
